/**
 * Pathname → `page` property (AC-711, spec §9.1).
 *
 * The property is a CLOSED union, not the raw pathname. A raw path would put
 * every game slug, category and provider into the Lexicon as its own value,
 * making `page` useless as a funnel step or a breakdown — and Mixpanel keeps
 * whatever it is first sent, so that is not undoable.
 *
 * Input is the pathname from `@/i18n/navigation`'s `usePathname` (and the
 * orchestrator's `ctx.route`, which is the same value): already locale-stripped,
 * so `/es/casino/bonuses` arrives here as `/casino/bonuses`.
 */

export type PageName =
    | "home"
    | "casino_category"
    | "casino_providers"
    | "casino_provider"
    | "casino_favourites"
    | "bonuses"
    | "bonuses_cashback"
    | "bonuses_cryptobonus"
    | "bonuses_vip"
    | "game"
    | "docs"
    | "auth_success"
    | "banned"
    | "soon"
    /** Any route not enumerated above — a new page shows up here until it is named. */
    | "other";

/** Static routes, matched before the dynamic segments below. */
const STATIC_PAGES: Record<string, PageName> = {
    "/": "home",
    "/casino/providers": "casino_providers",
    "/casino/favourites": "casino_favourites",
    "/casino/bonuses": "bonuses",
    "/casino/bonuses/cashback": "bonuses_cashback",
    "/casino/bonuses/cryptobonus": "bonuses_cryptobonus",
    "/casino/bonuses/vip": "bonuses_vip",
    "/docs": "docs",
    "/auth/success": "auth_success",
    "/banned": "banned",
    "/soon": "soon",
};

/**
 * Dynamic routes, by their first segments. The trailing slug (`[game]`,
 * `[category]`, `[provider]`, `[docType]`) is deliberately NOT part of `page` —
 * it belongs in a dedicated property on the events that care (e.g. `game_id`).
 */
const DYNAMIC_PAGES: {prefix: string; page: PageName}[] = [
    {prefix: "/games/", page: "game"},
    {prefix: "/casino/categories/", page: "casino_category"},
    {prefix: "/casino/providers/", page: "casino_provider"},
    {prefix: "/docs/", page: "docs"},
];

export const toPageName = (pathname: string): PageName => {
    // Trailing slashes would miss every lookup below; `/` itself must survive.
    const normalized =
        pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

    const staticPage = STATIC_PAGES[normalized];
    if (staticPage) return staticPage;

    const dynamic = DYNAMIC_PAGES.find(({prefix}) => normalized.startsWith(prefix));
    return dynamic?.page ?? "other";
};
